import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { observer } from "mobx-react-lite";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { ExtraStoragePlan } from "../../../../../services/organization-subscription-plans/SubscriptionPlan";
import { translationService } from "../../../../../services/translation/TranslationService";
import { NumberSelection } from "../../../../../components/number-selection/NumberSelection";
import { InfoTooltip } from "../info-tooltip/InfoTooltip";
import "./composition.scss";

interface ExtraStorageProps {
  extraStorage: ExtraStoragePlan;
  baseExtraStoragePlan: ExtraStoragePlan;
  totalExtraStorageAmountChanged: (newAmount: number) => void;
  nextBillingDate?: Date;
  isActiveInNextBillingPeriod: boolean;
  currentPlanWillEndAndIsNotCanceled: boolean;
  currentSubscriptionWillBeRenewed: boolean;
  getLocalizedPrice: (price: number) => string;
}

export const ExtraStorage = observer((props: ExtraStorageProps) => {
  const MAX_AMOUNT = 9999;
  const {
    extraStorage: extraStoragePlan,
    baseExtraStoragePlan,
    totalExtraStorageAmountChanged,
    nextBillingDate,
    isActiveInNextBillingPeriod,
    currentPlanWillEndAndIsNotCanceled,
    currentSubscriptionWillBeRenewed,
    getLocalizedPrice,
  } = props;

  const [storageAmount, setStorageAmount] = useState(
    extraStoragePlan.activeSubscriptionPackageAmount || 0
  );

  const [totalStoragePrice, setTotalStoragePrice] = useState(
    extraStoragePlan.activeSubscriptionPackageAmount
      ? extraStoragePlan.activeSubscriptionPackageAmount * baseExtraStoragePlan.priceAmount
      : baseExtraStoragePlan.previouslySelectedAmount
      ? baseExtraStoragePlan.previouslySelectedAmount * baseExtraStoragePlan.priceAmount
      : 0
  );

  const initialValue = useMemo(() => {
    return (
      extraStoragePlan.activeSubscriptionPackageAmount ||
      baseExtraStoragePlan.previouslySelectedAmount ||
      0
    );
  }, [
    extraStoragePlan.activeSubscriptionPackageAmount,
    baseExtraStoragePlan.previouslySelectedAmount,
  ]);

  const extraStorageAmountChanged = useCallback(
    (newAmount: number) => {
      setStorageAmount(newAmount);
      setTotalStoragePrice(baseExtraStoragePlan.priceAmount * newAmount);
    },
    [baseExtraStoragePlan.priceAmount]
  );

  useEffect(() => {
    totalExtraStorageAmountChanged(storageAmount);
  }, [storageAmount, totalExtraStorageAmountChanged]);

  useEffect(() => {
    if (baseExtraStoragePlan.previouslySelectedAmount) {
      setStorageAmount(baseExtraStoragePlan.previouslySelectedAmount);
      setTotalStoragePrice(
        baseExtraStoragePlan.previouslySelectedAmount * baseExtraStoragePlan.priceAmount
      );
    }
  }, [baseExtraStoragePlan.priceAmount, baseExtraStoragePlan.previouslySelectedAmount]);

  return (
    <>
      <Accordion id={extraStoragePlan.name} className="accordion">
        <AccordionSummary className="accordion-header" expandIcon={<ExpandMoreIcon />}>
          <div className="medium-label">
            {`${translationService.translate("SubscriptionPlans.card.extra-storage")} -
            ${
              extraStoragePlan.activeSubscriptionPackageAmount ||
              baseExtraStoragePlan.previouslySelectedAmount ||
              0
            }
            ${translationService.translate("SubscriptionPlans.card.extra-storage-TB")}`}
          </div>
        </AccordionSummary>
        <AccordionDetails className="accordion-content">
          <div className="number-selection-container">
            <NumberSelection
              initialValue={initialValue}
              minValue={currentPlanWillEndAndIsNotCanceled ? initialValue : 0}
              maxValue={MAX_AMOUNT}
              labelTranslationKey="SubscriptionPlans.card.TB"
              callback={extraStorageAmountChanged}
              minusButtonDisabled={
                storageAmount <= 0 ||
                (currentPlanWillEndAndIsNotCanceled && storageAmount <= initialValue)
              }
              plusButtonDisabled={storageAmount >= MAX_AMOUNT}
            />
            {isActiveInNextBillingPeriod && currentSubscriptionWillBeRenewed && nextBillingDate && (
              <InfoTooltip
                nextBillingDate={nextBillingDate}
                firstBoldedTextKey="SubscriptionPlans.card.info.adding"
                firstTextKey="SubscriptionPlans.card.extra-storage.info.adding"
                secondBoldedTextKey="SubscriptionPlans.card.info.removing"
                secondTextKey="SubscriptionPlans.card.extra-storage.info.removing"
              />
            )}
            {isActiveInNextBillingPeriod &&
              !currentSubscriptionWillBeRenewed &&
              nextBillingDate && (
                <InfoTooltip
                  nextBillingDate={nextBillingDate}
                  firstBoldedTextKey="SubscriptionPlans.card.info.adding-or-removing"
                  firstTextKey="SubscriptionPlans.card.extra-storage.downgrade.info"
                />
              )}
            {currentPlanWillEndAndIsNotCanceled && nextBillingDate && (
              <InfoTooltip
                nextBillingDate={nextBillingDate}
                firstBoldedTextKey="SubscriptionPlans.card.info.removing"
                firstTextKey="SubscriptionPlans.card.extra-storage.info.removing-ending-plan"
              />
            )}
          </div>
          <div className="small-opacity-label">
            {`(${getLocalizedPrice(baseExtraStoragePlan.priceAmount)} € /
            1 ${translationService.translate("SubscriptionPlans.card.TB")} /
            ${translationService.translate("SubscriptionPlans.card.three-months")})`}
          </div>
          <div>
            <span className="large-label">{`${getLocalizedPrice(totalStoragePrice)} € / `}</span>
            {`${storageAmount} ${translationService.translate("SubscriptionPlans.card.TB")} /
            ${translationService.translate("SubscriptionPlans.card.three-months")}`}
          </div>
        </AccordionDetails>
      </Accordion>
    </>
  );
});
